import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";

function toHex(bytes: ArrayBuffer): string {
  return Array.from(new Uint8Array(bytes), (value) => value.toString(16).padStart(2, "0")).join("");
}

async function solveChallenge(challenge: string, difficulty: number, isCancelled: () => boolean): Promise<string> {
  const encoder = new TextEncoder();
  const prefix = "0".repeat(difficulty);
  for (let counter = 0; counter < 1_000_000; counter += 1) {
    if (isCancelled()) throw new Error("cancelled");
    const digest = await crypto.subtle.digest("SHA-256", encoder.encode(`${challenge}:${counter}`));
    if (toHex(digest).startsWith(prefix)) return `${challenge}.${counter}`;
    // Yield regularly so the invisible check never blocks typing or assistive tech.
    if (counter % 50 === 49) await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  }
  throw new Error("challenge-limit");
}

/**
 * An invisible, first-party proof-of-work check. It adds bounded per-request
 * browser work for automated abuse without presenting a CAPTCHA or sharing
 * visitor data with an external challenge provider.
 */
export default function ScoreHumanCheck({ onToken, disabled = false }: { onToken: (token: string) => void; disabled?: boolean }) {
  const [notice, setNotice] = useState("");
  const challengeQuery = trpc.audit.humanCheck.useQuery(undefined, {
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  useEffect(() => {
    const challenge = challengeQuery.data;
    if (!challenge || disabled) return;
    let cancelled = false;
    onToken("");
    setNotice("Checking your request…");

    void solveChallenge(challenge.challenge, challenge.difficulty, () => cancelled)
      .then((token) => {
        if (cancelled) return;
        onToken(token);
        setNotice("");
      })
      .catch(() => {
        if (!cancelled) setNotice("We could not complete the automated-abuse check. Please refresh and try again.");
      });

    const refreshTimer = window.setTimeout(() => {
      if (!cancelled) challengeQuery.refetch();
    }, Math.max(1_000, (challenge.expiresInSeconds - 15) * 1_000));
    return () => {
      cancelled = true;
      window.clearTimeout(refreshTimer);
    };
  }, [challengeQuery.data, disabled, onToken]);

  if (challengeQuery.isLoading) {
    return <p className="text-xs" aria-live="polite" style={{ color: "#8899A6" }}>Checking your request…</p>;
  }
  if (challengeQuery.isError) {
    return <p className="text-xs" role="alert" style={{ color: "#FF6B35" }}>The automated-abuse check could not load. Please refresh and try again.</p>;
  }
  return notice ? <p className="text-xs" aria-live="polite" style={{ color: "#8899A6" }}>{notice}</p> : null;
}
